import { useEffect, useState } from 'react';
import { crm } from '@/lib/crm-client';
import { ableiteZustand, type KampagnenMitgliedschaft, type LeadZusatz } from './lead-zustand';

/**
 * Kampagnen-Mitgliedschaft je Kontakt, über alle Kampagnen hinweg.
 *
 * Es gibt dafür keinen gebündelten Endpunkt — anders als bei Tags und Listen,
 * die je Kontakt an genau einer Stelle stehen, kann ein Kontakt Mitglied
 * mehrerer Kampagnen sein. Die Anzahl der Kampagnen ist der begrenzende
 * Faktor, nicht die Anzahl der Kontakte: eine Abfrage je Kampagne statt einer
 * je Lead. Steckt ein Kontakt in mehreren, gewinnt die zuletzt gelesene —
 * ein seltener Randfall, den der Zustand ohnehin nur als Momentaufnahme
 * zeigt.
 */
async function kampagnenMitgliedschaft(
  idSet: Set<string>,
): Promise<Map<string, KampagnenMitgliedschaft>> {
  const kampagnen = await crm.campaigns.list();
  const mitgliederListen = await Promise.all(
    kampagnen.map((k) => crm.campaigns.members(k.id)),
  );
  const karte = new Map<string, KampagnenMitgliedschaft>();
  for (const liste of mitgliederListen) {
    for (const m of liste) {
      if (idSet.has(m.contactId)) {
        karte.set(m.contactId, { state: m.state, followupState: m.followupState });
      }
    }
  }
  return karte;
}

/**
 * Tags, Listenzugehörigkeit und Zustand für mehrere Leads auf einmal —
 * je eine Anfrage insgesamt statt einer je Zeile.
 *
 * `refreshToken` erzwingt ein Neuladen, wenn sich an genau diesen Kontakten
 * etwas geändert hat, ohne dass sich die Kontakt-ID-Menge selbst ändert
 * (z. B. nach dem Taggen einer Auswahl).
 */
export function useLeadZusatz(
  contactIds: string[],
  refreshToken: number,
): Map<string, LeadZusatz> {
  const [zusatz, setZusatz] = useState<Map<string, LeadZusatz>>(new Map());
  const schluessel = contactIds.join(',');

  useEffect(() => {
    const ids = schluessel ? schluessel.split(',') : [];
    if (ids.length === 0) {
      setZusatz(new Map());
      return;
    }
    let abgebrochen = false;
    void Promise.all([
      crm.tags.forContacts(ids),
      crm.leadLists.forContacts(ids),
      crm.mail.outreachCheck(ids),
      kampagnenMitgliedschaft(new Set(ids)),
      // Der Uhrstand der Erstansprache selbst — eine Anfrage fuer alle
      // Zeilen, dieselbe Buendelung wie bei tags.forContacts. Noetig fuer
      // Leads mit einer Erstansprache ausserhalb jeder Kampagne, siehe
      // `ableiteZustand`.
      crm.followups.forContacts(ids),
    ]).then(
      ([tagsMap, listMap, outreach, mitgliedschaft, eigeneUhren]) => {
        if (abgebrochen) return;
        const nochNie = new Set(
          outreach.filter((o) => o.firstContact).map((o) => o.contactId),
        );
        const neu = new Map<string, LeadZusatz>();
        for (const id of ids) {
          neu.set(id, {
            tags: tagsMap[id] ?? [],
            listIds: listMap[id] ?? [],
            zustand: ableiteZustand(
              mitgliedschaft.get(id),
              nochNie.has(id),
              eigeneUhren[id] ?? null,
            ),
          });
        }
        setZusatz(neu);
      },
      () => {
        /* Zusatzinfo bleibt beim vorherigen Stand — kein Fehlertext für eine Spalte */
      },
    );
    return () => {
      abgebrochen = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, refreshToken]);

  return zusatz;
}
